import { useNavigate, useParams } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from '@/shared/libs/generate-path'
import {
  useCreateTicketConfigMutation,
  useTicketConfigByIdQuery,
  useUpdateTicketConfigMutation,
} from '@/entities/ticket-config'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { TicketConfigForm } from '@/features/ticket-config/ticket-config-form'
import {
  basicInfoToUpdatePayload,
  featureIdsToUpdatePayload,
  formValuesToCreatePayload,
  ticketConfigToFormValues,
} from '@/features/ticket-config/ticket-config-form/schema'
import type { TicketConfigFormValues } from '@/features/ticket-config/ticket-config-form/schema'
import type { TicketConfigTourPayload } from '@/shared/api/types/ticket-config-types'

export function TicketConfigFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const { activeTenantId, isSuperAdmin } = useAdminScope()
  const params: { tenantId?: string; id?: string } = useParams({ strict: false })

  const { id } = params
  const isEdit = Boolean(id)

  const effectiveTenantId = isSuperAdmin
    ? (params.tenantId ?? (activeTenantId || undefined))
    : activeTenantId || undefined

  const { data: configResponse, isLoading: isConfigLoading } = useTicketConfigByIdQuery(
    id,
    effectiveTenantId,
  )

  const createMutation = useCreateTicketConfigMutation()
  const updateBasicInfoMutation = useUpdateTicketConfigMutation()
  const updateFeaturesMutation = useUpdateTicketConfigMutation()
  const updateToursMutation = useUpdateTicketConfigMutation()

  const listPath =
    isSuperAdmin && effectiveTenantId
      ? scopedAdminPath(
          generatePath('/tenant/:tenantId/ticket-configs', { tenantId: effectiveTenantId }),
        )
      : scopedAdminPath('/ticket-configs')

  const configDetail = configResponse?.data
  const configId = configDetail?.id
  const configTicketType = configDetail?.ticketType
  const configUpdatedAt = configDetail?.updatedAt
  const featureIdsSignature = configDetail?.ticketConfigFeature
    .map((item) => item.featureId)
    .join('|')

  const initialValues = useMemo<TicketConfigFormValues | undefined>(() => {
    if (!configDetail) return undefined
    return ticketConfigToFormValues(configDetail)
  }, [configDetail])
  const basicInfoKey = [
    configId,
    configTicketType,
    configUpdatedAt,
    initialValues?.localizedData.en.name,
    initialValues?.localizedData.en.description,
    initialValues?.localizedData['vi-south'].name,
    initialValues?.localizedData['vi-south'].description,
  ].join('::')
  const featuresKey = [configId, featureIdsSignature].join('::')
  // * VI: Card Tours dựng lại khi tính năng cấu hình chung hoặc override đổi, vì tour chưa tùy chỉnh đang hiển thị tính năng chung | EN: The Tours card remounts when the config-level features or the overrides change, because tours without their own set display the config-level ones.
  const toursKey = [
    featuresKey,
    configDetail?.ticketConfigTour
      .map((tour) => `${tour.tourId}:${tour.isCustomized ? tour.featureIds.join(',') : '-'}`)
      .join('|'),
  ].join('::')

  async function handleCreate(values: TicketConfigFormValues) {
    try {
      await createMutation.mutateAsync({
        ...(effectiveTenantId ? { tenantId: effectiveTenantId } : {}),
        payload: formValuesToCreatePayload(values),
      })
      showToast.success(t('ticketConfig.createSuccess', { defaultValue: 'Ticket config created' }))
      void navigate({ href: listPath })
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  async function handleSaveBasicInfo(
    basicInfo: Pick<TicketConfigFormValues, 'ticketType' | 'localizedData'>,
  ) {
    if (!id) return
    try {
      await updateBasicInfoMutation.mutateAsync({
        id,
        ...(effectiveTenantId ? { tenantId: effectiveTenantId } : {}),
        payload: basicInfoToUpdatePayload(basicInfo),
      })
      showToast.success(t('ticketConfig.basicInfoSaved', { defaultValue: 'Basic info saved' }))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  async function handleSaveFeatures(featureIds: string[]) {
    if (!id) return
    try {
      await updateFeaturesMutation.mutateAsync({
        id,
        ...(effectiveTenantId ? { tenantId: effectiveTenantId } : {}),
        payload: featureIdsToUpdatePayload(featureIds),
      })
      showToast.success(t('ticketConfig.featuresSaved', { defaultValue: 'Features saved' }))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  async function handleSaveTours(tours: TicketConfigTourPayload[]) {
    if (!id) return
    try {
      await updateToursMutation.mutateAsync({
        id,
        ...(effectiveTenantId ? { tenantId: effectiveTenantId } : {}),
        payload: { ticketConfigTours: tours },
      })
      showToast.success(t('ticketConfig.toursSaved', { defaultValue: 'Tour features saved' }))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  const pageTitle = isEdit
    ? t('ticketConfig.edit', { defaultValue: 'Edit Ticket Config' })
    : t('ticketConfig.create', { defaultValue: 'Create Ticket Config' })

  return (
    <PageContainer>
      <PageHeader
        title={pageTitle}
        breadcrumbs={[
          { label: t('ticketConfig.title', { defaultValue: 'Ticket Configs' }), path: listPath },
          { label: pageTitle },
        ]}
      />

      {isEdit ? (
        <TicketConfigForm
          mode="edit"
          {...(initialValues ? { initialValues } : {})}
          {...(configDetail?.ticketConfigFeature
            ? { initialFeatures: configDetail.ticketConfigFeature }
            : {})}
          {...(initialValues && basicInfoKey ? { basicInfoKey } : {})}
          {...(initialValues && featuresKey ? { featuresKey } : {})}
          {...(configDetail ? { initialTours: configDetail.ticketConfigTour, toursKey } : {})}
          loading={isConfigLoading}
          submittingBasicInfo={updateBasicInfoMutation.isPending}
          submittingFeatures={updateFeaturesMutation.isPending}
          submittingTours={updateToursMutation.isPending}
          onSaveBasicInfo={handleSaveBasicInfo}
          onSaveFeatures={handleSaveFeatures}
          onSaveTours={handleSaveTours}
        />
      ) : (
        <TicketConfigForm
          mode="create"
          submitting={createMutation.isPending}
          onCreate={handleCreate}
        />
      )}
    </PageContainer>
  )
}
